import AddIcon from '@mui/icons-material/Add';
import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, Button, DialogContent, Grid, Skeleton, Stack, Theme, Typography, useTheme } from '@mui/material';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useMemo, useState } from 'react';
import BackNavigationButton from '../../../components/common/BackNavigationButton';
import DialogAdaptive from '../../../components/common/DialogAdaptive';
import LargeMaterialUISwitch from '../../../components/common/LargeMaterialUISwitch';
import PageContainer from '../../../components/common/PageContainter';
import StyledCard from '../../../components/common/StyledCard';
import StyledCardTitleBar from '../../../components/common/StyledCardTitleBar';
import StyledDialogActions from '../../../components/common/StyledDialogActions';
import ToyDetails from '../../../components/services/sextforce/liveStreamLovense/ToyDetails';
import ToySettingsEditor from '../../../components/services/sextforce/liveStreamLovense/ToySettingsEditor';
import useLovense from '../../../hooks/useLovense';
import useSubscriber from '../../../hooks/useSubscriber';
import { SettingsContext } from '../../../store/SettingsContext';
import { isSextforceActive } from '../../../utils/common';

const SextforceLiveStreamLovense = () => {
    const settingsContext = useContext(SettingsContext);
    const theme: Theme = useTheme();
    const dialog = useDialog();
    const { enqueueSnackbar } = useSnackbar();

    const { data: subscriber, isLoading: isSubscriberLoading } = useSubscriber();

    // Check if Sextforce is active for this subscriber
    const sextforceActive = useMemo(() => {
        if (subscriber && isSextforceActive(subscriber)) {
            return true;
        }

        return false;
    }, [subscriber]);

    const [serviceActive, setServiceActive] = useState<boolean>(false);
    const [qrUrl, setQrUrl] = useState<string>('');
    const [qrCode, setQrCode] = useState<string>('');

    const {
        data: lovenseToys,
        isLoading: isLovenseLoading,
        refetch: refetchLovenseToys,
        getLovenseServiceActive,
        setLovenseServiceActive,
        isServiceStatusLoading,
        getLovenseQrCode,
        isConnectingToy,
        sendTestCommandToToy,
        isSendingTestCommand,
        deleteLovenseToy,
        isToyDeleting,
    } = useLovense(qrCode !== '');

    // Store how many toys are connected so that when the user adds a new toy, we can clear the QR code
    const [connectedToysCount, setConnectedToysCount] = useState<number>(0);

    useEffect(() => {
        if (lovenseToys) {
            if (connectedToysCount < lovenseToys.length) {
                // A new toy has been connected, clear the QR code
                setQrUrl('');
                setQrCode('');
                refetchLovenseToys();
            }

            setConnectedToysCount(lovenseToys.length);
        }
    }, [lovenseToys]);

    // On load, get the current service active status
    useEffect(() => {
        if (!sextforceActive) {
            setServiceActive(false);

            return;
        }

        getLovenseServiceActive()
            .then(active => {
                setServiceActive(active);
            })
            .catch(() => {
                setServiceActive(false);
            });
    }, [sextforceActive]);

    // Generate QR Code to connect a new Lovense toy
    const handleGenerateQrCode = async () => {
        const qrData = await getLovenseQrCode();

        if (qrData) {
            // Show QR Code to user
            setQrUrl(qrData.qrUrl);
            setQrCode(qrData.code);
        }
    };

    return (
        <PageContainer>
            {subscriber && '_id' in subscriber && subscriber._id && (
                <>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        <BackNavigationButton url={`/subscribers/${subscriber._id}/${settingsContext.services.sextforce.homeUrl}`} />
                        Lovense Vibes for {subscriber.username}
                    </Typography>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title="Lovense Vibes" />
                    </StyledCard>
                    <StyledCard>
                        <Typography gutterBottom>
                            Use Lovense Vibes to enhance your live streams! Stay connected and interactive with your audience.
                        </Typography>
                        <Typography gutterBottom>
                            When enabled, your connected Lovense toys will respond to tips and interactions during your live streams based
                            on your configured settings.
                        </Typography>
                        <Typography gutterBottom>
                            Use the settings below to manage your connected Lovense toys, adjust tip reaction settings, and connect new
                            devices.
                        </Typography>
                        <Typography gutterBottom>
                            Make sure your Lovense toys are connected and ready to go before starting your live stream! Use the Test button
                            below to verify your connection. Also note that on some older models, the toy may only respond to commands if
                            the toy antenna is "external" and nothing is blocking the signal.
                        </Typography>
                        <Typography>
                            To disconnect a Lovense toy, simply remove it from your connected devices list in your Lovense app.
                        </Typography>
                    </StyledCard>

                    <StyledCard>
                        <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center">
                            <Grid item xs>
                                Lovense Vibes is currently{' '}
                                {serviceActive ? (
                                    <span style={{ color: theme.palette.success.dark }}>running</span>
                                ) : (
                                    <span style={{ color: theme.palette.error.main }}>not running</span>
                                )}
                                .
                            </Grid>
                            <Grid item xs="auto">
                                {isSubscriberLoading || isServiceStatusLoading ? (
                                    <Skeleton width={'115px'} height={'51px'} />
                                ) : (
                                    <LargeMaterialUISwitch
                                        checked={serviceActive}
                                        disabled={!sextforceActive}
                                        onChange={e => {
                                            const newActiveStatus = e.target.checked;

                                            if (!sextforceActive) {
                                                return;
                                            }

                                            if (newActiveStatus === serviceActive) {
                                                return;
                                            }

                                            setLovenseServiceActive(newActiveStatus).then(success => {
                                                if (success) {
                                                    setServiceActive(newActiveStatus);
                                                }
                                            });
                                        }}
                                        color="primary"
                                    />
                                )}
                            </Grid>
                        </Grid>

                        {!sextforceActive && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                Sextforce service must be active to use this feature
                            </Alert>
                        )}
                    </StyledCard>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title="My Lovense Toys" isLoading={isLovenseLoading} />
                    </StyledCard>

                    <StyledCard>
                        <Stack direction="column" spacing={2} mb={2}>
                            {lovenseToys && lovenseToys.length > 0 ? (
                                lovenseToys.map(toy => (
                                    <ToyDetails
                                        toy={toy}
                                        key={toy.toyId}
                                        onTestCommand={() => {
                                            sendTestCommandToToy(toy.toyId)
                                                .then(() => {
                                                    enqueueSnackbar('Good vibes sent successfully!', {
                                                        variant: 'success',
                                                        autoHideDuration: 3000,
                                                    });
                                                })
                                                .catch(() => {
                                                    enqueueSnackbar(`Toy not responding. Toy or app are possibly disconnected or off.`, {
                                                        variant: 'error',
                                                        autoHideDuration: 3000,
                                                    });
                                                });
                                        }}
                                        isTestCommandRunning={isSendingTestCommand}
                                    />
                                ))
                            ) : (
                                <Typography>No Lovense toys connected yet.</Typography>
                            )}
                        </Stack>

                        <LoadingButton
                            loading={isConnectingToy}
                            variant="contained"
                            color="secondary"
                            fullWidth
                            size="large"
                            onClick={() => {
                                handleGenerateQrCode();
                            }}
                            startIcon={<AddIcon />}
                        >
                            <span>Connect New Lovense Toy</span>
                        </LoadingButton>
                    </StyledCard>

                    {qrUrl && qrCode && (
                        <DialogAdaptive
                            open={qrUrl !== ''}
                            onClose={() => {
                                setQrUrl('');
                                setQrCode('');
                            }}
                            maxWidth="xs"
                            fullWidth
                        >
                            <StyledCardTitleBar title="Lovense QR Code" />

                            <DialogContent>
                                <Stack direction="column" spacing={1}>
                                    <Typography gutterBottom>Scan the QR code below with your Lovense app to connect your toy.</Typography>
                                    <div style={{ textAlign: 'center' }}>
                                        <img src={qrUrl} alt="Lovense QR Code" style={{ maxWidth: '100%', height: 'auto' }} />
                                        <Typography variant="body2" sx={{ mt: 2 }}>
                                            Or enter this code manually in the Lovense app:
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            sx={{ mb: 1, fontWeight: 'bold', fontFamily: 'monospace', fontSize: '1.6em' }}
                                        >
                                            {qrCode}
                                        </Typography>
                                    </div>
                                </Stack>
                            </DialogContent>
                            <StyledDialogActions>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setQrUrl('');
                                        setQrCode('');
                                    }}
                                >
                                    Cancel
                                </Button>
                            </StyledDialogActions>
                        </DialogAdaptive>
                    )}

                    {lovenseToys?.map(toy => (
                        <>
                            <StyledCard noCard noBottomMargin>
                                <StyledCardTitleBar
                                    title={`${toy.nickName || toy.name} Settings`}
                                    isLoading={isLovenseLoading || isToyDeleting}
                                    badge={
                                        <Button
                                            variant="contained"
                                            color="error"
                                            size="small"
                                            disabled={isToyDeleting}
                                            onClick={() => {
                                                dialog
                                                    .confirm({
                                                        title: 'Confirm Delete',
                                                        message: `Are you sure you want to delete ${
                                                            toy.nickName || toy.name
                                                        }? This will disconnect the toy and remove all settings associated with it.`,
                                                        ok: {
                                                            text: 'Delete',
                                                            variant: 'contained',
                                                            color: 'error',
                                                        },
                                                        cancel: {
                                                            text: 'Cancel',
                                                            variant: 'outlined',
                                                        },
                                                    })
                                                    .then(async () => {
                                                        await deleteLovenseToy(toy.toyId);
                                                        enqueueSnackbar('Toy deleted successfully', {
                                                            variant: 'success',
                                                            autoHideDuration: 3000,
                                                        });
                                                        refetchLovenseToys();
                                                    })
                                                    .catch(() => {
                                                        // User cancelled, do nothing
                                                    });
                                            }}
                                        >
                                            Delete
                                        </Button>
                                    }
                                />
                            </StyledCard>

                            <StyledCard>
                                <Stack direction="column" spacing={2}>
                                    <Typography variant="body2" color="text.secondary" mb={2}>
                                        Configure intensity and duration based on tip amount.
                                    </Typography>

                                    <ToySettingsEditor toy={toy} key={toy.toyId} />
                                </Stack>
                            </StyledCard>
                        </>
                    ))}
                </>
            )}
        </PageContainer>
    );
};

export default SextforceLiveStreamLovense;
